/*
 * The date the brand panel shows is the moment the visitor opened the form.
 * It is stamped once per invoice and kept in localStorage, so a reload — or the
 * trip back from a 3-D Secure page — shows the same time instead of ticking
 * forward. The core keeps its own cache under the bare `invoice_id` key, hence
 * the prefix here.
 */

const KEY_PREFIX = 'form_opened_at:'

// Survives the whole page load, so a re-render never touches storage twice and
// the date stays put even where localStorage is unavailable (private mode).
let cached = null

function storageKey(params) {
  return KEY_PREFIX + (params?.invoice_id || params?.order || 'unknown')
}

function read(key) {
  try {
    const value = Number(localStorage.getItem(key))
    return Number.isFinite(value) && value > 0 ? value : 0
  } catch {
    return 0
  }
}

function write(key, timestamp) {
  try {
    // Only ever one invoice is in flight, so previous stamps are dead weight.
    for (const existing of Object.keys(localStorage)) {
      if (existing.startsWith(KEY_PREFIX) && existing !== key) {
        localStorage.removeItem(existing)
      }
    }
    localStorage.setItem(key, String(timestamp))
  } catch {
    // storage unavailable — `cached` still holds the stamp for this page load
  }
}

const pad = (value) => String(value).padStart(2, '0')

/**
 * Renders the moment the form was opened as "05.08.2025, 12:45" in the
 * visitor's own time zone. The format is fixed rather than locale-aware on
 * purpose: it is the one the design asks for, and a day-first date is what the
 * merchants this widget serves expect in every language it ships in.
 */
export function getOrderDate(params) {
  const key = storageKey(params)

  if (!cached || cached.key !== key) {
    let timestamp = read(key)
    if (!timestamp) {
      timestamp = Date.now()
      write(key, timestamp)
    }
    cached = {key, date: new Date(timestamp)}
  }

  const date = cached.date

  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, `
    + `${pad(date.getHours())}:${pad(date.getMinutes())}`
}
